import React from "react";
import { Link as RouterLink } from "react-router";
import { useTranslation } from "react-i18next";
import styled from "@emotion/styled";
import { Box, Link, Typography, useTheme } from "@mui/material";
import { GainLossPercents } from "@rentablo/components";
import { LoadingPlaceHolder } from "@rentablo/components";
import { useGainersAndLosers } from "@features/dashboard/hooks/useGainersAndLosers.ts";
import { useDashboardSelectedGroupsContext } from "@features/dashboard/DashboardSelectedGroupProvider.tsx";

const DashboardGainersAndLosersWrapper = styled.div`
  padding-top: 10px;
  width: 100%;
`;

const GainersAndLosersItem = styled(RouterLink)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 11px;
  text-decoration: none;
`;

const InvestmentName = styled(Typography)`
  margin-right: ${({ theme }) => theme.spacing(6)};
  max-width: 300px;
`;

export const DashboardGainersAndLosers = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { accountIds, isFetching: areAccountsLoading } =
    useDashboardSelectedGroupsContext();
  const { gainersAndLosers, isFetching } = useGainersAndLosers(
    accountIds || []
  );

  if (isFetching || areAccountsLoading) {
    return <LoadingPlaceHolder width="100%" />;
  }

  if (!gainersAndLosers.gainers.length && !gainersAndLosers.losers.length) {
    return (
      <Box
        width="100%"
        height="400px"
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          maxWidth="275px"
          textAlign="center"
        >
          <Typography
            variant="body3"
            color="textSecondary"
            mb={theme.spacing(1)}
          >
            {t("dashboardPage.dashboard.gainersAndLosers.noData")}
          </Typography>
          <Link
            component={RouterLink}
            to="/investments"
            underline="hover"
            sx={{ display: "block", width: "fit-content" }}
          >
            <Typography variant="body4">
              {t("dashboardPage.dashboard.gainersAndLosers.more")}
            </Typography>
          </Link>
        </Box>
      </Box>
    );
  }

  return (
    <DashboardGainersAndLosersWrapper>
      {gainersAndLosers.gainers.length > 0 && (
        <>
          <Box
            display="flex"
            justifyContent="space-between"
            mb={theme.spacing(3)}
          >
            <Typography variant="body2" color="textPrimary">
              {t("dashboardPage.dashboard.gainersAndLosers.gainers")}
            </Typography>
            <Typography
              variant="caption2"
              color="textSecondary"
              alignSelf="center"
            >
              {t("dashboardPage.dashboard.gainersAndLosers.period")}
            </Typography>
          </Box>
          {gainersAndLosers.gainers.map(({ name, id, value }) => (
            <GainersAndLosersItem key={id} to={`/investments/${id}`}>
              <InvestmentName variant="caption2" color="textPrimary" noWrap>
                {name}
              </InvestmentName>
              <GainLossPercents value={value} />
            </GainersAndLosersItem>
          ))}
        </>
      )}
      {gainersAndLosers.losers.length > 0 && (
        <>
          <Box
            display="flex"
            justifyContent="space-between"
            mt={gainersAndLosers.gainers.length > 0 ? theme.spacing(5) : 0}
            mb={theme.spacing(3)}
          >
            <Typography variant="body2" color="textPrimary">
              {t("dashboardPage.dashboard.gainersAndLosers.losers")}
            </Typography>
          </Box>
          {gainersAndLosers.losers.map(({ name, id, value }) => (
            <GainersAndLosersItem key={id} to={`/investments/${id}`}>
              <InvestmentName variant="caption2" color="textPrimary" noWrap>
                {name}
              </InvestmentName>
              <GainLossPercents value={value} />
            </GainersAndLosersItem>
          ))}
        </>
      )}

      <Link
        component={RouterLink}
        to="/investments"
        underline="hover"
        sx={{ display: "block", width: "fit-content" }}
      >
        <Typography variant="body4" mt={6.5} ml={3}>
          {t("dashboardPage.dashboard.gainersAndLosers.more")}
        </Typography>
      </Link>
    </DashboardGainersAndLosersWrapper>
  );
};
